// Firmark Calcs — Slice 0: the 9 design roles, ported MOCK-FREE and made
// engine-valid. Each role declares which engine calculator + preset backs it
// (or null when the engine can't yet — stud / beam_column), which design fields
// it reveals, which checks/diagrams it shows, its section list, and its default
// DesignInputs. The defaults come from the design's ROLES[*].defaults +
// defaultsFor(), translated to species/grade/size strings the engine accepts
// and stripped of the mock's illustrative datasets.

import type { DesignInputs, MaterialClass, RoleKind, RoleLoadKind } from "./ndsTypes";

export type NdsRole = {
  key: string;
  label: string;
  kind: RoleKind;
  mark: string;
  note: string;
  // engine calculator id (registry) backing this role, or null when the engine
  // has no calculator for it yet (capability-gated in calcAdapter).
  calculatorId: string | null;
  // engine preset id under that calculator (registry presets), or null.
  presetId: string | null;
  supported: boolean;
  // present iff !supported — the user-facing "coming soon" reason.
  comingSoon?: string;
  // Wood-class materials this template offers (founder model: material is a
  // within-template selector — all wood classes, NO metal). First entry is the
  // default; absent → sawn only (no selector shown). Picking a material
  // dispatches the calc in calcAdapter (sawn/glulam → wood_beam_asd,
  // lvl/lsl/psl → wood_scl_beam_asd, ijoist → wood_ijoist_asd).
  materials?: MaterialClass[];
  loadKind: RoleLoadKind;
  // which design input fields this role reveals (mirrors ROLES[*].fields).
  fields: Record<string, boolean>;
  checks: string[];
  diagrams: string[];
  sectionList: string[];
  defaults: DesignInputs;
};

// (The old shared beam-column "coming soon" gate — engine-gaps.md G1 — is
// retired: wood_stud_wall_asd + wood_biaxial_beam_column_asd landed with the
// Phase-1 wood merge and both beamcol roles are live.)

// Render order (matches the design's role dropdown order).
export const ROLE_ORDER: string[] = [
  "floor_joist",
  "rafter",
  "beam",
  "header",
  "ridge",
  "stud",
  "post",
  "beam_column",
  "tension",
];

export const NDS_ROLES: Record<string, NdsRole> = {
  floor_joist: {
    key: "floor_joist",
    label: "Floor joist",
    kind: "bending",
    mark: "FJ-1",
    note: "repetitive on · L/360 LL",
    calculatorId: "wood_beam_asd",
    presetId: "floor_joist",
    supported: true,
    materials: ["sawn", "ijoist", "lvl"] as MaterialClass[],
    loadKind: "area",
    fields: {
      span: true, spacing: true, bearing: true, tributary: true,
      compBracing: true, plies: false, slope: false, height: false, unbraced: false,
    },
    checks: ["bending", "shear", "bearing", "deflLL", "deflTL"],
    diagrams: ["loading", "shear", "moment", "deflection", "bearing", "reaction", "section"],
    sectionList: ["2x6", "2x8", "2x10", "2x12"],
    defaults: {
      source: "Database", species: "Southern Pine", grade: "No. 2",
      size: "2x10", plies: 1, span: 14, spacing: 16, bearing: 1.5, slope: 0,
      bracing: "continuous", service: "dry", repetitive: true, selfWeight: false,
      deflLL: 360, deflTL: 240,
      loads: [
        { name: "Dead", type: "D", mag: 15 },
        { name: "Floor live", type: "L", mag: 40 },
      ],
    },
  },

  rafter: {
    key: "rafter",
    label: "Rafter (sloped)",
    kind: "bending",
    mark: "R-1",
    note: "roof live + snow · slope on · L/240",
    calculatorId: "wood_beam_asd",
    presetId: "rafter",
    supported: true,
    materials: ["sawn", "ijoist", "lvl"] as MaterialClass[],
    loadKind: "area",
    fields: {
      span: true, spacing: true, bearing: true, tributary: true,
      compBracing: true, slope: true, plies: false, height: false, unbraced: false,
    },
    checks: ["bending", "shear", "bearing", "deflLL", "deflTL"],
    diagrams: ["loading", "shear", "moment", "deflection", "bearing", "reaction", "section"],
    sectionList: ["2x6", "2x8", "2x10", "2x12"],
    defaults: {
      source: "Database", species: "Southern Pine", grade: "No. 2",
      size: "2x10", plies: 1, span: 13, spacing: 24, bearing: 1.5, slope: 6,
      bracing: "continuous", service: "dry", repetitive: true, selfWeight: false,
      deflLL: 240, deflTL: 180,
      loads: [
        { name: "Dead", type: "D", mag: 12 },
        { name: "Roof live", type: "Lr", mag: 20 },
        { name: "Snow", type: "S", mag: 35 },
      ],
    },
  },

  beam: {
    key: "beam",
    label: "Roof / floor beam (girder)",
    kind: "bending",
    mark: "B-1",
    note: "CL bracing · no Cr",
    calculatorId: "wood_beam_asd",
    presetId: "floor_beam",
    supported: true,
    materials: ["sawn", "glulam", "lvl", "lsl", "psl"] as MaterialClass[],
    loadKind: "line",
    fields: {
      span: true, bearing: true, compBracing: true, plies: true,
      tributary: false, spacing: false, slope: false, height: false, unbraced: false,
    },
    checks: ["bending", "shear", "bearing", "deflLL", "deflTL", "stability"],
    diagrams: ["loading", "shear", "moment", "deflection", "bearing", "reaction", "section"],
    // Catalog-covered sections only: the member DB carries no 4x dimension
    // lumber and no B&S timbers (6x10 / 6x12) yet, so the girder preset is a
    // built-up 2x12 until the Table 4D Beams & Stringers import lands.
    sectionList: ["2x8", "2x10", "2x12"],
    defaults: {
      source: "Database", species: "Southern Pine", grade: "No. 2",
      size: "2x12", plies: 3, span: 10, bearing: 3, bracing: "continuous",
      service: "dry", repetitive: false, selfWeight: false, deflLL: 360, deflTL: 240,
      loads: [
        { name: "Dead", type: "D", mag: 120 },
        { name: "Floor live", type: "L", mag: 240 },
      ],
    },
  },

  header: {
    key: "header",
    label: "Header / lintel",
    kind: "bending",
    mark: "H-1",
    note: "short span · multi-ply · bearing",
    calculatorId: "wood_beam_asd",
    presetId: "floor_beam",
    supported: true,
    materials: ["sawn", "glulam", "lvl"] as MaterialClass[],
    loadKind: "line",
    fields: {
      span: true, bearing: true, compBracing: true, plies: true,
      tributary: false, spacing: false, slope: false, height: false, unbraced: false,
    },
    checks: ["bending", "shear", "bearing", "deflLL", "deflTL", "stability"],
    diagrams: ["loading", "shear", "moment", "deflection", "bearing", "reaction", "section"],
    sectionList: ["2x8", "2x10", "2x12"],
    defaults: {
      source: "Database", species: "Southern Pine", grade: "No. 2",
      size: "2x12", plies: 2, span: 8, bearing: 3, bracing: "continuous",
      service: "dry", repetitive: false, selfWeight: false, deflLL: 360, deflTL: 240,
      loads: [
        { name: "Roof+wall DL", type: "D", mag: 200 },
        { name: "Roof snow", type: "S", mag: 300 },
      ],
    },
  },

  ridge: {
    key: "ridge",
    label: "Ridge beam",
    kind: "bending",
    mark: "RB-1",
    note: "rafter reactions · long span · stability",
    calculatorId: "wood_beam_asd",
    presetId: "generic",
    supported: true,
    materials: ["sawn", "glulam", "lvl", "psl"] as MaterialClass[],
    loadKind: "line",
    fields: {
      span: true, bearing: true, compBracing: true, plies: true, tributary: true,
      spacing: false, slope: false, height: false, unbraced: false,
    },
    checks: ["bending", "shear", "bearing", "deflLL", "deflTL", "stability"],
    diagrams: ["loading", "shear", "moment", "deflection", "bearing", "reaction", "section"],
    // Catalog-covered sections only (the member DB has no B&S timbers yet) —
    // a built-up 2x12 ridge until the Table 4D Beams & Stringers import lands.
    sectionList: ["2x10", "2x12"],
    defaults: {
      source: "Database", species: "Southern Pine", grade: "No. 2",
      size: "2x12", plies: 3, span: 10, bearing: 4, bracing: "unbraced",
      service: "dry", repetitive: false, selfWeight: false, deflLL: 240, deflTL: 180,
      loads: [
        { name: "Dead", type: "D", mag: 150 },
        { name: "Snow", type: "S", mag: 250 },
      ],
    },
  },

  stud: {
    key: "stud",
    label: "Stud (wall)",
    kind: "beamcol",
    mark: "ST-1",
    note: "axial + wind · CP · 3.9.2 interaction",
    // wood_stud_wall_asd: sheathed repetitive stud as a BEAM-COLUMN — gravity
    // axial (CP, weak axis braced by sheathing) + out-of-plane wind bending
    // (Cr on Fb) per the NDS 3.9.2 interaction. The old beam-column gate is
    // retired for this role; the engine landed with the Phase-1 wood merge.
    calculatorId: "wood_stud_wall_asd",
    presetId: "bearing_wall_2x6",
    supported: true,
    loadKind: "stud",
    fields: {
      height: true, spacing: true, bearing: true, unbraced: true, tributary: true,
      span: false, slope: false, compBracing: false, plies: false,
    },
    checks: ["compression", "bending", "combined", "bearing"],
    // The stud runs MODEL_SIMPLE over the wall height with the out-of-plane
    // wind pressure on w_plf, so it emits the SAME response set as a beam.
    // Verified against the engine — compute("wood_stud_wall_asd", …) returns
    // limit states flexure, shear, bearing, deflection_live, deflection_total,
    // compression, combined_bending_axial, and a diagram carrying spanIn, a
    // real deflMaxIn and per-combo vMaxLb / mMaxLbft. shear and bearing were
    // declared NOWHERE, which is the whole reason this role dropped to the
    // small tab strip instead of the response panel.
    diagrams: ["loading", "shear", "moment", "deflection", "bearing", "buckling", "axial", "reaction", "section"],
    sectionList: ["2x4", "2x6", "2x8"],
    defaults: {
      source: "Database", species: "Southern Pine", grade: "No. 2",
      size: "2x6", height: 10, spacing: 16, bearing: 1.5, ke: "1.0",
      service: "dry", repetitive: true, selfWeight: false,
      deflLL: 240, deflTL: 180,
      loads: [
        { name: "Roof+floor DL", type: "D", mag: 1100, axial: true },
        { name: "Floor LL", type: "L", mag: 2100, axial: true },
        { name: "Wind", type: "W", mag: 22, lateral: true },
      ],
    },
  },

  post: {
    key: "post",
    label: "Post / column",
    kind: "axial",
    mark: "P-1",
    note: "pure axial · CP · biaxial Le",
    calculatorId: "wood_column_asd",
    presetId: "interior_post",
    supported: true,
    loadKind: "axial",
    fields: {
      height: true, bearing: true, unbraced: true, span: false, spacing: false,
      slope: false, compBracing: false, tributary: false, plies: false,
    },
    checks: ["compression", "bearing"],
    diagrams: ["loading", "buckling", "axial", "reaction", "section"],
    // Catalog-covered posts only: no 4x posts / 6x10 in the member DB (the
    // Table 4D posts-&-timbers ladder runs 5x5 / 6x6 / 6x8 / 8x8 / 8x10 ...).
    sectionList: ["6x6", "6x8", "8x8", "8x10"],
    defaults: {
      source: "Database", species: "Southern Pine", grade: "No. 2",
      size: "6x6", height: 9, bearing: 5.5, ke: "1.0",
      service: "dry", repetitive: false, selfWeight: false,
      loads: [
        { name: "Tributary DL", type: "D", mag: 4000, axial: true },
        { name: "Floor LL", type: "L", mag: 6000, axial: true },
      ],
    },
  },

  beam_column: {
    key: "beam_column",
    label: "Beam-column (biaxial)",
    kind: "beamcol",
    mark: "BC-1",
    note: "axial + M1/M2 · Eq. 3.9-3",
    // wood_biaxial_beam_column_asd: applied per-type axial force + strong (M1)
    // and weak (M2) moments through the NDS 3.9 interaction. Its vocabulary is
    // APPLIED MOMENTS — a wind pressure belongs on the Stud (wall) template
    // (the adapter gates pressure rows honestly). Old beam-column gate retired.
    calculatorId: "wood_biaxial_beam_column_asd",
    presetId: "wind_column",
    supported: true,
    loadKind: "stud",
    fields: {
      height: true, bearing: true, unbraced: true, tributary: false,
      span: false, spacing: false, slope: false, compBracing: true, plies: false,
    },
    checks: ["compression", "bending", "combined", "bearing"],
    diagrams: ["loading", "moment", "deflection", "buckling", "axial", "reaction", "section"],
    // DF-L posts mirror the registry presets (catalog-covered timbers).
    sectionList: ["4x6", "4x8", "6x6", "6x8"],
    defaults: {
      source: "Database", species: "Douglas Fir-Larch", grade: "No. 2",
      size: "6x6", height: 12, bearing: 5.5, ke: "1.0",
      service: "dry", repetitive: false, selfWeight: false,
      loads: [
        { name: "Axial DL", type: "D", mag: 4000, axial: true },
        { name: "Axial snow", type: "S", mag: 3000, axial: true },
        { name: "Wind M1", type: "W", mag: 1800, momentAxis: "M1" },
        { name: "Wind M2", type: "W", mag: 900, momentAxis: "M2" },
      ],
    },
  },

  tension: {
    key: "tension",
    label: "Tension member / tie",
    kind: "tension",
    mark: "T-1",
    note: "tension ∥ grain · net section · conn. stub",
    calculatorId: "wood_tension_asd",
    presetId: "rafter_tie",
    supported: true,
    loadKind: "axial",
    fields: {
      height: true, unbraced: false, span: false, spacing: false, slope: false,
      compBracing: false, tributary: false, bearing: false, plies: false,
    },
    checks: ["tension"],
    diagrams: ["loading", "axial", "reaction", "section"],
    // Catalog-covered sections only (no 4x dimension lumber in the member DB).
    sectionList: ["2x4", "2x6", "2x8"],
    defaults: {
      source: "Database", species: "Southern Pine", grade: "No. 2",
      size: "2x6", height: 14, service: "dry", repetitive: false, selfWeight: false,
      loads: [
        { name: "Dead", type: "D", mag: 500, axial: true },
        { name: "Snow", type: "S", mag: 1500, axial: true },
      ],
    },
  },
};

// A deep-ish clone of a role's default DesignInputs (loads array copied so a
// caller mutating the returned inputs never aliases the registry).
export function defaultsForRole(roleKey: string): DesignInputs {
  const role = NDS_ROLES[roleKey];
  if (!role) {
    throw new Error(`unknown role: ${roleKey}`);
  }
  const d = role.defaults;
  return {
    ...d,
    loads: (d.loads ?? []).map((l) => ({ ...l })),
    supports: d.supports ? d.supports.map((s) => ({ ...s })) : undefined,
    designValueOverrides: d.designValueOverrides ? [...d.designValueOverrides] : undefined,
  };
}
